import React, { useEffect, useState, useRef } from "react";
import "./main.css";
import Middle from "./middle";
import location from "./location.svg";
import magnifyingGlass from "./magnifying.svg";
import user from "./user.svg";

export default function Main() {
  const [trips, setTrips]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [userCount, setUserCount] = useState(0);
  const [avgRating, setAvgRating] = useState(null);   // ✅ site-wide average rating
  const [ratingCount, setRatingCount] = useState(0);
  const tripsRef                  = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tripsRes, usersRes, ratingRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/get-trips`),
          fetch(`${process.env.REACT_APP_API_URL}/users-count`),
          fetch(`${process.env.REACT_APP_API_URL}/ratings/average`),
        ]);
        const tripsData  = await tripsRes.json();
        const usersData  = await usersRes.json();
        const ratingData = await ratingRes.json();
        if (tripsRes.ok) setTrips(tripsData.trips || []);
        if (usersRes.ok) setUserCount(usersData.count || 0);
        if (ratingRes.ok) {
          setAvgRating(ratingData.average || 0);
          setRatingCount(ratingData.count || 0);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const scrollToTrips = () => {
    tripsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const today       = new Date();
  const activeTrips = trips.filter((t) => new Date(t.endDate) >= today).length;
  // ✅ Count trips that have already ended
  const endedTrips  = trips.filter((t) => new Date(t.endDate) < today).length;

  return (
    <div>
      {/* ── HERO ─────────────────────────────────────── */}
      <div className="main-container">
        <div className="hero-eyebrow">
          <span className="hero-dot" />
          Students · Travel · Together
        </div>

        <h1 className="title">Travel Together,<br />Save More</h1>

        <p className="hero-subtitle">
          Connect with fellow university students, share rides,
          split costs, and create unforgettable travel memories.
        </p>

        <button className="button" onClick={scrollToTrips}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          Discover Trips
        </button>

        {/* ✅ Contact Us label + Instagram follow chip — sits right under the CTA */}
        <p className="hero-contact-label">Contact Us</p>
        <a
          href="https://www.instagram.com/trailnestdd?igsh=MTljbHZkc2t2bDZjNQ=="
          target="_blank"
          rel="noopener noreferrer"
          className="hero-instagram"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#igGradientHero)" />
            <rect x="6.5" y="6.5" width="11" height="11" rx="3.2" stroke="#fff" strokeWidth="1.6" />
            <circle cx="12" cy="12" r="3.2" stroke="#fff" strokeWidth="1.6" />
            <circle cx="16.6" cy="7.4" r="1.1" fill="#fff" />
            <defs>
              <linearGradient id="igGradientHero" x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFDD55" />
                <stop offset="0.5" stopColor="#FF543E" />
                <stop offset="1" stopColor="#C837AB" />
              </linearGradient>
            </defs>
          </svg>
          @trailnestdd
        </a>

        {/* ✅ Stats: total users | active trips | trips completed */}
        <div className="hero-stats">
          <div className="hero-stat">
            <span className="hero-stat-number">{userCount}+</span>
            <span className="hero-stat-label">Users</span>
          </div>
          <div className="hero-divider" />
          <div className="hero-stat">
            <span className="hero-stat-number">{activeTrips}</span>
            <span className="hero-stat-label">Active Trips</span>
          </div>
          <div className="hero-divider" />
          <div className="hero-stat">
            <span className="hero-stat-number">{endedTrips}</span>
            <span className="hero-stat-label">Trips Completed</span>
          </div>
          {/* ✅ Site-wide average rating — only shown once at least one rating exists */}
          {avgRating !== null && ratingCount > 0 && (
            <>
              <div className="hero-divider" />
              <div className="hero-stat">
                <span className="hero-stat-number hero-stat-rating">
                  {avgRating.toFixed(1)}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="hero-stat-star">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                </span>
                <span className="hero-stat-label">Avg Rating</span>
              </div>
            </>
          )}
        </div>

        {/* Decorative wave */}
        <div className="hero-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              fill="#fff8f0"
            />
          </svg>
        </div>
      </div>

      {/* ── TRIPS SECTION ────────────────────────────── */}
      <div ref={tripsRef} className="trips-section">
        <div className="section-header">
          <div className="section-badge">
            <span className="section-badge-dot" />
            Live Now
          </div>
          <h2 className="section-title">Featured Trips</h2>
          <p className="section-sub">
            Find a ride, join a trip, or create your own and invite your college mates.
          </p>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Finding trips near you…</p>
          </div>
        ) : trips.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 48 48" fill="none">
              <rect x="4" y="17" width="36" height="21" rx="4" fill="#ffd4c2" />
              <rect x="8" y="13" width="24" height="11" rx="3" fill="#ffd4c2" opacity=".6" />
              <circle cx="13" cy="38" r="4.5" fill="#ffd4c2" />
              <circle cx="35" cy="38" r="4.5" fill="#ffd4c2" />
            </svg>
            <h3>No trips yet</h3>
            <p>Be the first to create a trip and invite your friends!</p>
          </div>
        ) : (
          <Middle trips={trips} />
        )}
      </div>

      {/* ── HOW IT WORKS ─────────────────────────────── */}
      <div className="footer">
        <div className="footer-inner">
          <div className="section-badge" style={{ marginBottom: 14 }}>
            <span className="section-badge-dot" />
            Simple Process
          </div>
          <h1>How It Works</h1>

          <div className="items">
            <div className="step-card">
              <div className="step-icon">
                <img src={magnifyingGlass} alt="Discover Trips" />
              </div>
              <div className="step-num">01</div>
              <h3>Discover Trips</h3>
              <p>Browse trips organised by students from your university or nearby colleges.</p>
            </div>

            <div className="step-connector" />

            <div className="step-card">
              <div className="step-icon">
                <img src={user} alt="Join or Create" />
              </div>
              <div className="step-num">02</div>
              <h3>Join or Create</h3>
              <p>Join existing trips or post your own ride and invite fellow students.</p>
            </div>

            <div className="step-connector" />

            <div className="step-card">
              <div className="step-icon">
                <img src={location} alt="Travel Together" />
              </div>
              <div className="step-num">03</div>
              <h3>Travel Together</h3>
              <p>Enjoy amazing experiences while saving money through group bookings.</p>
            </div>
          </div>

          {/* ✅ Footer social row */}
          <div className="footer-social">
            <span className="footer-social-label">Follow the journey</span>
            <a
              href="https://www.instagram.com/trailnestdd?igsh=MTljbHZkc2t2bDZjNQ=="
              target="_blank"
              rel="noopener noreferrer"
              className="footer-instagram"
              aria-label="TrailNest on Instagram"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#igGradientFooter)" />
                <rect x="6.5" y="6.5" width="11" height="11" rx="3.2" stroke="#fff" strokeWidth="1.6" />
                <circle cx="12" cy="12" r="3.2" stroke="#fff" strokeWidth="1.6" />
                <circle cx="16.6" cy="7.4" r="1.1" fill="#fff" />
                <defs>
                  <linearGradient id="igGradientFooter" x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FFDD55" />
                    <stop offset="0.5" stopColor="#FF543E" />
                    <stop offset="1" stopColor="#C837AB" />
                  </linearGradient>
                </defs>
              </svg>
              @trailnestdd
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}